import { FC, ReactNode, Suspense, useId } from 'react';
import styled from 'styled-components';

import { Vertical } from 'atoms/layout/flex';
import { lazy } from 'lib/retry';

import neonChristmasBackgroundUrl from './assets/neonChristmasBackground.jpg';

const BackgroundWrapper = styled.div`
  background-image: url(${neonChristmasBackgroundUrl});
  background-size: cover;
  background-position: 20% top;
  position: relative;
  isolation: isolate;
  height: 100%;
`;

const SnowBackground = lazy(async () =>
  import('@sorare/trois/src/components/SnowBackground').then(module => ({
    default: module.SnowBackground,
  }))
);

const SnowPicture = styled.div`
  position: absolute;
  inset: 0;
  background-color: var(--c-nd-50-opaque);
`;
const SnowWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const NeonChristmasBackground = ({ children, ...props }: Props) => {
  return (
    <BackgroundWrapper {...props}>
      <SnowWrapper>
        <Suspense fallback={<SnowPicture />}>
          <SnowBackground fallback={<SnowPicture />} />
        </Suspense>
      </SnowWrapper>
      {children}
    </BackgroundWrapper>
  );
};
const NeonChristmasLogo = () => {
  const glowId = useId();
  const glowId2 = useId();
  return (
    <svg
      width={223}
      viewBox="0 0 282 81"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g filter={`url(#${glowId})`}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M140.84 36.3383L140.823 36.32L147.255 32.8153C147.26 32.7246 147.262 32.6322 147.262 32.5384C147.262 28.2852 143.116 25.3779 137.14 25.3779C131.003 25.3779 126.75 28.3928 126.75 32.5384C126.75 34.0906 127.331 35.4433 128.287 36.6847L150.43 52.142C149.806 46.3045 144.591 42.671 139.888 39.3939C135.819 36.5588 132.133 33.9905 132.133 30.4925C132.133 27.9083 134.233 25.9163 137.033 25.9163C140.978 25.9163 143.375 29.7859 142.611 34.1054L140.39 36.5862L140.84 36.3383ZM142.324 48.2419C143.998 50.2413 145.108 52.5742 145.108 55.4736C145.108 60.1575 142.147 63.3878 137.894 63.3878C133.026 63.3878 130.538 59.3451 131.456 54.9113L134.343 52.8749L133.404 53.1697L133.54 53.2431L127.034 55.1598C126.847 55.6239 126.75 56.1638 126.75 56.7657C126.75 60.6421 130.895 63.9262 137.571 63.9262C145.27 63.9262 150.492 59.6191 150.492 53.32C150.492 53.131 150.488 52.9442 150.478 52.7596L142.324 48.2419ZM248.319 36.32L248.337 36.3383L247.886 36.5862L250.107 34.1054C250.872 29.7859 248.474 25.9163 244.529 25.9163C241.729 25.9163 239.63 27.9083 239.63 30.4925C239.63 33.9905 243.316 36.5588 247.384 39.3939C252.087 42.671 257.302 46.3045 257.926 52.142L235.783 36.6848C234.827 35.4433 234.246 34.0906 234.246 32.5384C234.246 28.3928 238.499 25.3779 244.637 25.3779C250.613 25.3779 254.758 28.2852 254.758 32.5384C254.758 32.6323 254.756 32.7246 254.751 32.8154L248.319 36.32ZM252.605 55.4736C252.605 52.3062 251.28 49.8148 249.342 47.6958L257.97 52.6669C257.982 52.8817 257.989 53.0993 257.989 53.32C257.989 59.6191 252.766 63.9262 245.067 63.9262C238.391 63.9262 234.246 60.6421 234.246 56.7657C234.246 56.1638 234.343 55.6239 234.53 55.1598L241.036 53.2431L240.9 53.1697L241.839 52.8749L238.953 54.9113C238.034 59.3451 240.522 63.3878 245.39 63.3878C249.644 63.3878 252.605 60.1575 252.605 55.4736ZM174.784 37.7069C173.869 30.9233 169.831 26.4547 165.309 26.4547H164.124V59.7807C164.124 61.7188 165.363 63.3878 167.301 63.3878H155.51C157.448 63.3878 158.741 61.7188 158.741 59.7807V26.4547C154.272 26.4547 148.996 30.9233 148.027 37.7069L149.642 25.9163H173.223L174.784 37.7069ZM190.6 59.7807L182.094 25.9163H177.17L177.594 30.4477L185.97 63.3878H189.578L190.629 59.893C190.619 59.8555 190.609 59.8181 190.6 59.7807ZM190.6 59.7807L198.945 29.5235C199.537 27.4776 198.407 26.4547 194.746 26.4547V25.9163H208.367V26.4547C206.429 26.4547 205.137 27.5853 205.137 29.5235V59.7807C205.137 61.3796 206.016 62.7954 207.412 63.242C209.632 62.7816 211.213 61.2996 211.57 59.7807L219.161 28.9312C218.623 27.3699 217.223 26.4547 215.5 26.4547V25.9163H224.06L227.508 42.4679L222.608 45.3567L222.607 45.352L219.43 30.0619L214.639 49.4975L222.607 45.352L214.477 50.1436L214.475 50.1512L214.449 50.1666L214.33 50.741L212.108 59.7807C211.678 61.6112 213.939 63.3878 217.761 63.3878H208.367H205.971H196.576C198.514 63.3878 199.807 61.7188 199.807 59.7807V28.6621L190.6 59.7807ZM191.202 61.2458L190.313 63.3878H194.477C193.082 63.3878 191.91 62.4396 191.202 61.2458ZM177.227 29.0057L177.168 25.9163H173.641V26.4547C175.31 26.4547 176.602 27.4238 177.194 28.8774V59.7807C177.194 61.6112 174.556 63.3878 171.595 63.3878H183.386C179.509 63.3878 177.787 61.665 177.787 59.7807L177.194 28.8774L177.227 29.0057ZM225.622 59.7807L222.842 46.4763L227.539 42.6174L231.113 59.7807C231.49 61.6112 233.051 63.3878 234.99 63.3878H223.199C225.137 63.3878 225.999 61.6112 225.622 59.7807ZM90.8969 45.0772V59.7807C90.8969 61.7188 89.6048 63.3878 87.6666 63.3878H99.4572C97.5191 63.3878 96.2269 61.7188 96.2269 59.7807V43.3778C94.3497 43.954 92.4559 44.5652 90.8969 45.0772ZM90.8969 44.8699V29.5235C90.8969 27.5853 89.6048 25.9163 87.6666 25.9163H99.9956C107.264 25.9163 112.055 28.6621 112.055 32.5384C112.055 34.8179 110.453 36.579 107.903 38.1646L100.997 40.8876C104.195 39.215 106.725 36.2512 106.725 32.6999C106.725 29.0928 104.034 26.4547 99.9956 26.4547H96.2269V42.7684L90.8969 44.8699ZM109.5 42.6664C111.122 44.4001 112.055 47.0762 112.055 50.4128V59.7807C112.055 60.9113 112.594 61.665 113.348 61.665C114.024 61.665 115.152 61.1626 115.568 60.2063C114.649 62.4632 113.152 63.9262 110.871 63.9262C108.394 63.9262 106.725 62.2572 106.725 59.7807V50.4128C106.725 44.8468 105.525 41.8057 102.675 41.6065C103.241 41.4883 103.708 41.4132 104.046 41.3936C106.289 41.2638 108.355 41.5475 109.5 42.6664ZM115.568 60.2063L115.589 60.1522L115.609 60.1037C115.596 60.1385 115.582 60.1727 115.568 60.2063ZM60.676 59.7805V48.9937L66.006 46.2217V59.7805C66.006 61.7187 67.2981 63.3877 69.2363 63.3877H57.4457C59.3839 63.3877 60.676 61.7187 60.676 59.7805ZM60.676 29.5233V46.3375L66.0316 44.198L70.7609 42.2418L77.9043 39.2681V39.2871L83.2343 37.0824V29.5233C83.2343 27.5851 84.5264 25.9162 86.4646 25.9162H74.674C76.6122 25.9162 77.9043 27.5851 77.9043 29.5233V39.2681L66.006 43.629V29.5233C66.006 27.5851 67.2981 25.9162 69.2363 25.9162H57.4457C59.3839 25.9162 60.676 27.5851 60.676 29.5233ZM77.9043 59.7805V40.0336L83.2343 37.2615V59.7805C83.2343 61.7187 84.5264 63.3877 86.4646 63.3877H74.674C76.6122 63.3877 77.9043 61.7187 77.9043 59.7805ZM101.831 34.2996C101.268 34.8446 100.984 35.549 100.763 36.2932V36.2977C100.355 34.6098 99.323 33.613 97.6529 33.2054C99.3098 32.7801 100.346 31.7877 100.758 30.1087C101.175 31.7612 102.203 32.7668 103.846 33.1921C103.084 33.418 102.398 33.7503 101.831 34.2996Z"
          fill="#FF0A0A"
        />
        <path
          d="M118.752 59.7806V29.5235C118.752 27.5853 117.46 25.9163 115.522 25.9163H127.312C125.374 25.9163 124.082 27.5853 124.082 29.5235V59.7806C124.082 61.7188 125.374 63.3878 127.312 63.3878H115.522C117.46 63.3878 118.752 61.7188 118.752 59.7806Z"
          fill="#FF0A0A"
        />
        <path
          d="M57.9038 48.4621C55.9573 58.3569 50.9828 63.2773 42.7101 63.2773C31.6257 63.2773 25.1373 51.1116 25.1373 40.7842C25.1373 35.4853 27.5164 24.6172 40.1688 24.6172C51.1991 24.6172 50.6584 34.9446 49.8473 39.0539C50.9828 33.5929 52.7131 27.6992 55.4166 26.1853C52.659 26.8882 48.7119 23.9684 40.1688 23.9684C28.8682 23.9684 20.3792 33.4306 20.3792 43.8662C20.3792 56.3023 29.4089 63.9262 42.3857 63.9262C49.1444 63.9262 55.2003 60.682 55.2003 60.682L57.9038 48.5703V48.4621Z"
          fill="#FF0A0A"
        />
        <path
          d="M113.655 25.9162H121.599H128.965L121.599 28.8452L113.655 25.9162Z"
          fill="#FF0A0A"
        />
        <path
          d="M128.967 63.3878L121.023 63.3878L113.656 63.3878L121.023 60.4589L128.967 63.3878Z"
          fill="#FF0A0A"
        />
        <path
          d="M46.7818 63.7699L101.639 63.4612L77.137 63.1553L46.7818 63.7699Z"
          fill="#FF0A0A"
        />
        <path
          d="M16.7218 63.6157H71.5795L47.08 63.1718L16.7218 63.6157Z"
          fill="#FF0A0A"
        />
        <path
          d="M184.489 63.6157H239.346L214.847 63.1718L184.489 63.6157Z"
          fill="#FF0A0A"
        />
        <path
          d="M210.142 26.138H265L240.5 25.6942L210.142 26.138Z"
          fill="#FF0A0A"
        />
      </g>
      <g filter={`url(#${glowId2})`}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M150.609 19.8958C151.434 20.6285 152.703 21.0685 154.449 21.0685C156.194 21.0685 157.458 20.6256 158.282 19.8723C159.095 19.1275 159.573 17.9922 159.573 16.4002C159.573 14.9888 159.113 13.9391 158.304 13.2323C157.482 12.5142 156.21 12.0737 154.449 12.0737C152.687 12.0737 151.421 12.5142 150.601 13.2509C149.792 13.9776 149.325 15.0764 149.325 16.5996C149.325 18.0883 149.795 19.1736 150.609 19.8958ZM156.379 19.2396C155.893 19.9348 155.192 20.2331 154.449 20.2331C153.708 20.2331 153.011 19.9375 152.525 19.2641C152.062 18.6229 151.841 17.7102 151.841 16.5569C151.841 15.3746 152.056 14.4494 152.518 13.8038C153.007 13.1206 153.709 12.8378 154.449 12.8378C155.186 12.8378 155.884 13.1181 156.374 13.7791C156.839 14.4082 157.057 15.3034 157.057 16.4287C157.057 17.6393 156.838 18.5821 156.379 19.2396ZM186.84 12.2589V18.8849L181.955 12.2589H179.036V20.8833H180.526V13.4115L185.968 20.8833H188.33V12.2589H186.84ZM124.639 20.0053H130.378V20.8833H122.48V12.2589H130.378V13.1369H124.639V16.0039H129.31V16.768H124.639V20.0053ZM101.219 12.2589V18.885L96.3352 12.2589H93.416V20.8834H94.9064V13.4116L100.348 20.8834H102.71V12.2589H101.219Z"
          fill="#FF0A0A"
        />
      </g>
      <defs>
        <filter
          id={glowId}
          x="0.0336933"
          y="7.28028"
          width="281.654"
          height="73.334"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="8.34405" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1 0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 1 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_769_8676"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_769_8676"
            result="shape"
          />
        </filter>
        <filter
          id={glowId2}
          filterUnits="objectBoundingBox"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="5.68106" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1 0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 1 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_769_8676"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_769_8676"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
};

const Wrapper = styled(Vertical).attrs({ center: true })`
  padding: var(--double-unit) 0;
`;

type Props = { children: ReactNode };

export const NeonChristmasWrapper: FC<Props> = ({ children }) => (
  <NeonChristmasBackground>
    <Wrapper>
      <NeonChristmasLogo />
      {children}
    </Wrapper>
  </NeonChristmasBackground>
);
